import AirDatepicker, {
  type AirDatepickerLocale,
  type AirDatepickerOptions,
  type AirDatepickerPosition,
} from 'air-datepicker';
import localeEn from 'air-datepicker/locale/en';
import localeId from 'air-datepicker/locale/id';
import localeKo from 'air-datepicker/locale/ko';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import excelIcon from '@/assets/icon/excel-icon-download.svg';
import POIRed from '@/assets/smartcity/poi-red.svg';
import CardEventSmartCity from '@/domains/traffic-monitoring/components/CardEventSmartCity/CardEventSmartCity';
import CardInOut from '@/domains/traffic-monitoring/components/CardInOut/CardInOut';
import CardVolumeVehicle from '@/domains/traffic-monitoring/components/CardVolumeVehicle/CardVolumeVehicle';
import ModalExportExcel from '@/domains/traffic-monitoring/components/ModalExportExcel/ModalExportExcel';
import ProgressCard from '@/domains/traffic-monitoring/components/ProgressCard/ProgressCard';
import SmartCityMaps from '@/domains/traffic-monitoring/components/SmartCityMaps/SmartCityMaps';
import { useRecentTrafficEvents } from '@/domains/traffic-monitoring/hooks/useRecentTrafficEvents';
import { useTrafficCongestion } from '@/domains/traffic-monitoring/hooks/useTrafficCongestion';
import { useTrafficCongestionByLocation } from '@/domains/traffic-monitoring/hooks/useTrafficCongestionByLocation';
import { useTrafficEventList } from '@/domains/traffic-monitoring/hooks/useTrafficEventList';
import { useTrafficEventSummary } from '@/domains/traffic-monitoring/hooks/useTrafficEventSummary';
import { useTrafficExport } from '@/domains/traffic-monitoring/hooks/useTrafficExport';
import { useTrafficVolumeByVehicle } from '@/domains/traffic-monitoring/hooks/useTrafficVolume';
import { useTrafficVolumeHourly } from '@/domains/traffic-monitoring/hooks/useTrafficVolumeHourly';
import { useTrafficVolumeTrend } from '@/domains/traffic-monitoring/hooks/useTrafficVolumeTrend';
import { useVehicleInOut } from '@/domains/traffic-monitoring/hooks/useVehicleInOut';
import Header from '@/shared/components/layout/Header/Header';
import BarLineChart from '@/shared/components/ui/BarLineChart.tsx/BarLineChart.tsx';
import Button from '@/shared/components/ui/Button';
import GeneralInput from '@/shared/components/ui/GeneralInput/GeneralInput';
import LineChart from '@/shared/components/ui/LineChart/LineChart';
import type { ModalMapTooltipItem } from '@/shared/components/ui/ModalMapTooltip/ModalMapTooltip.types';
import { formatDateToYYYYMMDD } from '@/shared/utils/date';
import { zoomSafeDatepickerPosition } from '@/shared/utils/datepickerPosition';

const coords = {
  lat: -7.11148,
  lng: 110.9122,
};

// Opens a traffic event's detail page in a new window - shared by the event
// list panel and the map's cluster tooltip so both click paths land on the
// same screen.
const openEventDetail = (eventId: string) => {
  const params = new URLSearchParams({ type: 'event', id: eventId });
  window.open(
    `/dashboard/event-dashboard?${params.toString()}`,
    '_blank',
    'width=760,height=500,resizable=yes,scrollbars=yes'
  );
};

const TrafficDashboard = () => {
  const { t, i18n } = useTranslation();

  // PSC_PC0203001_BE_001 - real vehicle volume for the left panel, aggregated
  // into the 4 display buckets (today, all sites/CCTVs, both directions).
  const { buckets: vehicleBuckets } = useTrafficVolumeByVehicle({});

  // PSC_PC0203001_BE_006 - today's traffic-event counts by type + day-over-day
  // change for the right "Event Category" panel (global scope, like-for-like).
  const { cards: eventCards, totalToday: eventTotalToday } = useTrafficEventSummary({});

  // PSC_PC0203001_BE_003 - overall congestion % + peak hour + that hour's own
  // volume (area-wide, today).
  const { congestionPct, peakHour, peakVolume } = useTrafficCongestion({});

  // Per-CCTV congestion %, today - feeds the map's congestion heatmap layer.
  const { heatPoints: congestionHeatPoints } = useTrafficCongestionByLocation({});

  // PSC_PC0203001_BE_004 - incoming/outgoing volume + avg speed (area-wide, today).
  // The City Gateway cameras (cctv 21/23/27) publish their in/out counts as
  // into_out_to_grobogan and leave `direction` null.
  const { incoming, outgoing } = useVehicleInOut({ direction_field: 'into_out_to_grobogan' });

  // PSC_PC0203001_BE_005 - last 24h volume + avg-speed trend (area-wide, 1h buckets).
  const { trend: volumeTrend } = useTrafficVolumeTrend({});

  // PSC_PC0203001 - per-hour volume by vehicle (peak-hours line chart, today).
  const { xAxisData: peakHoursX, series: peakHoursSeries } = useTrafficVolumeHourly({});
  const translatedPeakHoursSeries = peakHoursSeries.map((series) => ({
    ...series,
    name: t(`traffic>vehicle>${series.name}`),
  }));

  const today = new Date();

  const [dateTime, setDateTime] = useState({
    start_date: formatDateToYYYYMMDD(today),
  });

  // PSC_PC0203001_BE_007 - Event List. The date picker sends a bare
  // YYYY-MM-DD `start_time`, which the backend scopes to that single
  // calendar day (00:00-24:00 WIB, capped at now), newest first.
  const [startTime, setStartTime] = useState(formatDateToYYYYMMDD(today));
  const {
    cards: eventListCards,
    totalCount: eventListTotal,
    hasNextPage: hasMoreEvents,
    isFetchingNextPage: isLoadingMoreEvents,
    fetchNextPage: fetchMoreEvents,
  } = useTrafficEventList({ start_time: startTime });

  // The list is unbounded — a day runs to thousands of events — so the next
  // batch is fetched as the operator approaches the bottom instead of being
  // loaded up front. The threshold is a screenful ahead so the rows are usually
  // in place before the scroll reaches them.
  const handleEventListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!hasMoreEvents || isLoadingMoreEvents) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 320) {
      fetchMoreEvents();
    }
  };

  // PSC_PC0203001_BE_007 - Map pins, scoped to the last hour regardless of the
  // date picker (a separate rolling fetch from the day-scoped list above), to
  // match the map's "Recent Event (Last 1 Hour)" legend.
  const { events: recentEvents } = useRecentTrafficEvents();

  const eventMarkers = useMemo(() => {
    const byCctv = new Map<
      number,
      {
        id: string;
        lat: number;
        lng: number;
        name: string;
        items: ModalMapTooltipItem[];
        count: number;
      }
    >();

    for (const event of recentEvents) {
      if (event.cctv_id == null || event.latitude == null || event.longitude == null) {
        continue;
      }

      const nextItem: ModalMapTooltipItem = {
        title: event.event_label,
        timestamp: event.timestamp,
        id: event.id,
      };

      const existing = byCctv.get(event.cctv_id);

      if (existing) {
        existing.items.push(nextItem);
        existing.count += event.count ?? 1;
        continue;
      }

      byCctv.set(event.cctv_id, {
        id: `cctv-${event.cctv_id}`,
        lat: event.latitude,
        lng: event.longitude,
        name: event.cctv_name ?? `CCTV ${event.cctv_id}`,
        items: [nextItem],
        count: event.count ?? 1,
      });
    }

    return Array.from(byCctv.values()).map((entry) => ({
      id: entry.id,
      lat: entry.lat,
      lng: entry.lng,
      type: 'red' as const,
      count: entry.count,
      tooltip: {
        title: entry.name,
        description: `${entry.items.length} event${entry.items.length > 1 ? 's' : ''}`,
      },
      modalTooltip: {
        title: entry.name,
        item: entry.items,
        onItemClick: (clickedItem: ModalMapTooltipItem) => {
          if (clickedItem.id) openEventDetail(clickedItem.id);
        },
      },
    }));
  }, [recentEvents]);

  useEffect(() => {
    let locale: AirDatepickerLocale;
    if (i18n.language === 'eng') {
      locale = localeEn;
    } else if (i18n.language === 'ind') {
      locale = localeId;
    } else {
      locale = localeKo;
    }
    const today = new Date();
    const optionsDate: AirDatepickerOptions = {
      autoClose: true,
      locale: locale,
      position: zoomSafeDatepickerPosition('top center'),
      // position: 'top center' as unknown as AirDatepickerPosition,
      selectedDates: [today],
      timepicker: false,
      timeFormat: 'HH:mm',
      dateFormat: 'yyyy-MM-dd',
      onSelect: (selection) => {
        setDateTime((prevValues) => ({
          ...prevValues,
          start_date: selection.formattedDate as string,
        }));
        // Feed the BE_007 lower bound: the selected day's date (00:00 WIB
        // server-side). Events are then listed from that date up to now.
        const picked = Array.isArray(selection.date) ? selection.date[0] : selection.date;
        if (picked) {
          setStartTime(formatDateToYYYYMMDD(picked));
        }
      },
    };

    const datepicker1 = new AirDatepicker('[name="start_date"]', optionsDate);

    return () => {
      datepicker1.destroy();
    };
  }, [i18n.language]);

  // PSC_PC0203001_BE_008 / BE_009 - Excel export of either panel over a date
  // range the operator picks in the modal. Each panel keeps its own state so
  // one export's error never shows up over the other's dialog.
  const [openExport, setOpenExport] = useState<'volume' | 'event' | null>(null);
  const volumeExport = useTrafficExport('volume');
  const eventExport = useTrafficExport('event');

  const closeExport = () => {
    volumeExport.clearError();
    eventExport.clearError();
    setOpenExport(null);
  };

  // A finished export closes the dialog; a failed one leaves it open so the
  // message sits next to the range that produced it.
  const handleExport = async (
    kind: 'volume' | 'event',
    startDate: string,
    endDate: string,
    interval: string
  ) => {
    const exporter = kind === 'volume' ? volumeExport : eventExport;
    const succeeded = await exporter.runExport(startDate, endDate, { interval });
    if (succeeded) setOpenExport(null);
  };

  const eventTypeMap: Record<string, string> = {
    EVT_TP_SPD: 'overspeed',
    EVT_TP_JW: 'jaywalk',
    EVT_TP_WWD: 'contraflow',
    EVT_TP_STP: 'suddenstop',
  };

  return (
    <section className="_trafficDashboard bg-db-wrap flex flex-col gap-[12px] w-full h-screen overflow-hidden">
      <Header />
      <div className="_trafficDashboard p-3 w-full h-[calc(95vh-15px)] grid grid-cols-[1fr_2fr_1fr] gap-3 overflow-hidden">
        {/* section left */}
        <section className="w-full flex! gap-3 overflow-auto  flex-col h-full  ">
          <div className="border  flex flex-col gap-3 w-full flex-[1.5] min-h-[400px]  rounded-[12px] bg-db-card bg-db-border overflow-auto ">
            <div className="p-6 flex flex-col gap-3 w-full h-full">
              <div className="inline-flex gap-3 justify-between items-center">
                <span className="title1bold text-db-text capitalize">
                  {t('traffic>master>traffic volume by vehicle')}
                </span>
                <Button customButton="btn-export shrink-0" onClick={() => setOpenExport('volume')}>
                  <img src={excelIcon} alt="" aria-hidden="true" />
                  {t('cmn>master>export excel', 'Export Excel')}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3 items-start h-fit">
                {vehicleBuckets.map((bucket) => (
                  <CardVolumeVehicle
                    key={bucket.title}
                    title={t(`traffic>vehicle>${bucket.title}`)}
                    totalVehicle={bucket.total}
                    typeArrow={bucket.typeArrow}
                    percentage={bucket.changePct}
                  />
                ))}
              </div>
              <div className="w-full h-full pb-[12px]">
                <span className="uppercase title2 text-text-grey">
                  {t('traffic>master>Peak hour graph')}
                </span>
                <div className="h-[calc(100%-50px)] min-h-[220px]">
                  <LineChart
                    xAxisData={peakHoursX}
                    // series={peakHoursSeries}
                    series={translatedPeakHoursSeries}
                    bottom={'70px'}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="border w-full h-full flex-1 min-h-[250px] rounded-[12px] bg-db-card bg-db-border overflow-auto">
            <div className="w-full h-full p-6 flex flex-col gap-3">
              <ProgressCard
                title={t('traffic>master>overall congestion level')}
                value={congestionPct}
                peakHour={peakHour}
                peakVolume={peakVolume}
              />
              <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(180px,1fr))] items-center gap-3 pb-[20px] ">
                <CardInOut
                  title="INCOMING"
                  totalVolume={incoming.volume}
                  totalSpeed={incoming.speed}
                />
                <CardInOut
                  title="OUTGOING"
                  typeCard="outgoing"
                  totalVolume={outgoing.volume}
                  totalSpeed={outgoing.speed}
                />
              </div>
            </div>
          </div>
        </section>
        {/* section left */}

        {/* section center */}
        <section className="w-full flex! gap-3 overflow-auto  flex-col h-full  ">
          <div className="border  flex flex-col gap-3 w-full flex-[1.5] min-h-[400px]  rounded-[12px] bg-db-card bg-db-border overflow-auto ">
            <SmartCityMaps
              coords={coords}
              markers={eventMarkers}
              heatmapPoints={congestionHeatPoints}
              showModal={true}
              hideCoordinates
              legend={[{ icon: POIRed, label: t('traffic>master>Recent Event (Last 1 Hour)') }]}
            />
          </div>

          <div className="border w-full h-full flex-1 min-h-[300px] rounded-[12px] bg-db-card bg-db-border overflow-auto">
            <div className="w-full flex flex-col h-[calc(100%-0px)] p-4">
              <span className="ccapitalize title1bold">{t('traffic>volume trend')}</span>

              <div className="h-full w-full overflow-x-auto">
                <div className="w-full min-w-[800px] h-full">
                  <BarLineChart data={volumeTrend} lineColor="accentBlue" barColor="accentBlue" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* section center */}

        {/* section right */}
        <section className="w-full flex! gap-3 overflow-hidden  flex-col h-full  ">
          <div className="border p-6  gap-3 flex flex-col w-full h-fit rounded-[12px] bg-db-card bg-db-border overflow-auto">
            <div className="inline-flex gap-3 justify-between gap-3 items-center">
              <span className="title1bold text-db-text capitalize">
                {t('traffic>event>event category')}
              </span>
              <div className="inline-flex gap-3 items-center">
                {/* total event category occurs today */}
                {/* <span className="text-text-blue-100 title1medium">{eventTotalToday}</span> */}
                <Button customButton="btn-export shrink-0" onClick={() => setOpenExport('event')}>
                  <img src={excelIcon} alt="" aria-hidden="true" />
                  {t('cmn>master>export excel', 'Export Excel')}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 items-center gap-3">
              {eventCards.map((card) => (
                <CardVolumeVehicle
                  key={card.title}
                  title={t(`traffic>event type>${eventTypeMap[card.code]}`)}
                  totalVehicle={card.total}
                  typeArrow={card.typeArrow}
                  percentage={card.changePct}
                />
              ))}
            </div>
          </div>
          <div className="border p-6 flex flex-col gap-3 w-full h-full flex-2 rounded-[12px] bg-db-card bg-db-border overflow-auto">
            <div className=" grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))]  gap-3 justify-between items-center">
              <span className="title1bold  text-db-text capitalize w-full">
                {t('cmn>master>event list')}{' '}
                <span className="title2 text-text-grey">
                  {eventListTotal.toLocaleString()} {t('cmn > total')}
                </span>{' '}
              </span>
              <GeneralInput
                isDob={true}
                type="text"
                name={'start_date'}
                value={dateTime.start_date}
                customInput=""
                inputFormStyle="w-full bg-[#F3F6FE]! dark:bg-[#F3F6FE]/10! border-[#C2D2E2]! dark:border-[#37424D]! rounded-[12px]! h-[40px]! title2!"
              />
            </div>
            <div
              className="w-full h-full flex flex-col gap-3 overflow-auto"
              onScroll={handleEventListScroll}
            >
              {eventListCards.length === 0 ? (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="title2 text-text-grey text-center">No event</span>
                </div>
              ) : (
                <>
                  {eventListCards.map((event) => {
                    const handleOpenEvent = () => openEventDetail(event.id);

                    return (
                      <CardEventSmartCity
                        key={event.key}
                        title={t(`traffic>event type>${eventTypeMap[event.eventType]}`)}
                        address={event.address}
                        dateTime={event.dateTime}
                        onclick={handleOpenEvent}
                      />
                    );
                  })}
                  {isLoadingMoreEvents && (
                    <span className="title2 text-text-grey text-center py-2 shrink-0">
                      Loading more…
                    </span>
                  )}
                  {/* Only shown when the list stops short of the total — i.e. the
                      endpoint's 10 000-row paging ceiling was reached, so the
                      operator knows older events exist but are not reachable here. */}
                  {!hasMoreEvents && eventListCards.length < eventListTotal && (
                    <span className="title2 text-text-grey text-center py-2 shrink-0">
                      Showing the newest {eventListCards.length.toLocaleString()} of{' '}
                      {eventListTotal.toLocaleString()} events
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
        {/* section right */}
      </div>

      <ModalExportExcel
        isOpen={openExport === 'volume'}
        onClose={closeExport}
        title={t(
          'traffic>export>export traffic volume by vehicle',
          'Export Traffic Volume by Vehicle'
        )}
        fieldPrefix="volume"
        isExporting={volumeExport.isExporting}
        errorMessage={volumeExport.errorMessage}
        onExport={(startDate, endDate, interval) =>
          handleExport('volume', startDate, endDate, interval)
        }
      />
      <ModalExportExcel
        isOpen={openExport === 'event'}
        onClose={closeExport}
        title={t('traffic>export>export traffic event', 'Export Traffic Event')}
        fieldPrefix="event"
        isExporting={eventExport.isExporting}
        errorMessage={eventExport.errorMessage}
        onExport={(startDate, endDate, interval) =>
          handleExport('event', startDate, endDate, interval)
        }
      />
    </section>
  );
};

export default TrafficDashboard;
